// SPDX-License-Identifier: GPL-3.0-or-later
// Keyboard input of the game page (THEME.md section 11.1).
// A key code maps to a game action or to a shell command (pause, start).
// The controller sends engine input events and releases every held key on blur.

import type { InputAction, InputEvent } from "../src/shared/contracts.ts";

/** What a key does. An engine action, or a shell command. */
export type KeyCommand = InputAction | "pause";

/** Key codes (KeyboardEvent.code) for each command. All keys can be pressed with one hand. */
export const KEY_MAP: Readonly<Record<string, KeyCommand>> = {
  ShiftLeft: "left_flipper",
  KeyZ: "left_flipper",
  ArrowLeft: "left_flipper",
  ShiftRight: "right_flipper",
  Slash: "right_flipper",
  ArrowRight: "right_flipper",
  Space: "plunger",
  ArrowDown: "plunger",
  Enter: "plunger",
  NumpadEnter: "plunger",
  KeyX: "nudge_up",
  Digit1: "start",
  Numpad1: "start",
  Escape: "pause",
  KeyP: "pause",
};

/** Look up the command of a key code. Unknown code: null. */
export function commandForKey(code: string): KeyCommand | null {
  return Object.hasOwn(KEY_MAP, code) ? (KEY_MAP[code] ?? null) : null;
}

/** The part of a key event that the controller reads. */
export interface KeyEventLike {
  readonly code: string;
  readonly repeat: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  readonly target?: unknown;
  preventDefault(): void;
}

/** True when the key target is a control that needs its own keys (a form field or a button). */
export function isInteractiveTarget(target: unknown): boolean {
  if (typeof target !== "object" || target === null) return false;
  const node = target as { tagName?: unknown; isContentEditable?: unknown };
  if (node.isContentEditable === true) return true;
  if (typeof node.tagName !== "string") return false;
  return ["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A", "SUMMARY"].includes(
    node.tagName.toUpperCase(),
  );
}

/** What the page tells the controller. */
export interface KeyboardDeps {
  /** Send one input to the engine. */
  readonly send: (ev: InputEvent) => void;
  /** Called for the pause key. */
  readonly onPause: () => void;
  /** True while a dialog is open. Then the game takes no key. */
  readonly modalOpen: () => boolean;
  /** Look up the command of a key code. Unset: the default map. The page passes the rebound map. */
  readonly commandFor?: (code: string) => KeyCommand | null;
}

/** A keyboard controller. `keyDown` and `keyUp` take the page key events. */
export interface KeyboardController {
  keyDown(ev: KeyEventLike): void;
  keyUp(ev: KeyEventLike): void;
  /** Release every held action. Call it on blur, on pause and on a hidden tab. */
  releaseAll(): void;
}

const HELD_ACTIONS: ReadonlySet<KeyCommand> = new Set<KeyCommand>([
  "left_flipper",
  "right_flipper",
  "plunger",
]);

/**
 * Make the controller. A held action stays pressed while at least one of its keys is down.
 * A key repeat sends nothing. A key with Ctrl, Meta or Alt is not a game key.
 */
export function createKeyboardController(deps: KeyboardDeps): KeyboardController {
  const core: KeyCore = {
    deps,
    lookup: deps.commandFor ?? commandForKey,
    down: new Map<InputAction, Set<string>>(),
  };
  return {
    keyDown: (ev) => onKeyDown(core, ev),
    keyUp: (ev) => onKeyUp(core, ev),
    releaseAll: () => releaseAllKeys(core),
  };
}

/** The state of one keyboard controller. */
interface KeyCore {
  readonly deps: KeyboardDeps;
  readonly lookup: (code: string) => KeyCommand | null;
  /** Key codes that are down, for each held action. */
  readonly down: Map<InputAction, Set<string>>;
}

/** A key with Ctrl, Meta or Alt is not a game key. */
function hasModifier(ev: KeyEventLike): boolean {
  return ev.ctrlKey === true || ev.metaKey === true || ev.altKey === true;
}

function releaseKey(core: KeyCore, action: InputAction): void {
  core.down.delete(action);
  core.deps.send({ action, pressed: false });
}

/** A held action goes down with its first key. */
function pressHeld(core: KeyCore, action: InputAction, code: string): void {
  const keys = core.down.get(action) ?? new Set<string>();
  const first = keys.size === 0;
  keys.add(code);
  core.down.set(action, keys);
  if (first) core.deps.send({ action, pressed: true });
}

/** Run a key press that the game takes. */
function runCommand(core: KeyCore, command: KeyCommand, code: string): void {
  if (command === "pause") core.deps.onPause();
  else if (HELD_ACTIONS.has(command)) pressHeld(core, command, code);
  else core.deps.send({ action: command, pressed: true });
}

function onKeyDown(core: KeyCore, ev: KeyEventLike): void {
  if (hasModifier(ev)) return;
  const command = core.lookup(ev.code);
  if (command === null) return;
  if (isInteractiveTarget(ev.target) || core.deps.modalOpen()) return;
  ev.preventDefault();
  if (!ev.repeat) runCommand(core, command, ev.code);
}

function onKeyUp(core: KeyCore, ev: KeyEventLike): void {
  const command = core.lookup(ev.code);
  if (command === null || !HELD_ACTIONS.has(command) || command === "pause") return;
  const keys = core.down.get(command);
  if (keys === undefined || !keys.delete(ev.code)) return;
  // A key up always counts, also while a dialog is open: no stuck flipper.
  if (keys.size === 0) releaseKey(core, command);
}

function releaseAllKeys(core: KeyCore): void {
  for (const action of [...core.down.keys()]) releaseKey(core, action);
}
